import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { SessionProvider, useSession } from './context/SessionContext';
import { SessionManager } from './services/sessionManager';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Test from './pages/Test';
import Admin from './pages/Admin';
import { LanguageWrapper } from './components/LanguageWrapper';

const AppContent: React.FC = () => {
  const { isAuthenticated, apiData, isLoading } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  // 1. Capture incoming referral on first landing
  useEffect(() => {
    SessionManager.captureReferral();
  }, []);

  // 2. Referral Protocol URL Synchronization
  // This effect ensures that the address bar always contains the correct referral code
  // and maintains a flat URL structure (no language prefixes).
  useEffect(() => {
    if (isLoading) return;

    const urlParams = new URLSearchParams(location.search);
    const currentRef = urlParams.get('ref') || urlParams.get('referral');
    
    let targetRef = currentRef;

    // Determine the source of truth for the referral code
    if (isAuthenticated && apiData?.referralCode) {
      // Authenticated User: Always use their personal sharing code to make the app "share-ready"
      targetRef = apiData.referralCode;
    } else {
      // Guest/Landing Mode: Use the code they landed with (stored in sessionStorage)
      const storedRef = sessionStorage.getItem('joisst_referral');
      if (!currentRef && storedRef) {
        targetRef = storedRef;
      }
    }

    // Synchronize the URL if the ref is missing, incorrect, or using a legacy key
    if (targetRef && (currentRef !== targetRef || urlParams.has('referral'))) {
      const newParams = new URLSearchParams(location.search);
      newParams.set('ref', targetRef);
      newParams.delete('referral'); 
      
      const newSearch = `?${newParams.toString()}`;
      
      // Prevent infinite loops by checking if the search actually changed
      if (newSearch !== location.search) {
        navigate({
          pathname: location.pathname,
          search: newSearch
        }, { replace: true });
      }
    }
  }, [isAuthenticated, apiData?.referralCode, isLoading, location.pathname, location.search, navigate]);

  return (
    <LanguageWrapper>
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/test" element={<Test />} />
          <Route path="/admin" element={<Admin />} />
          {/* Catch-all for flat routes redirects back to root */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </LanguageWrapper>
  );
};

const App: React.FC = () => {
  return (
    <SessionProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </SessionProvider>
  );
};

export default App;